import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import FormFieldContext from '../FormFieldContext';
import { FieldData, WeekdayCheckboxesFieldType } from 'types/FieldTypes';
import FieldWrapper from '../FieldWrapper';
import FieldViewMode from './elements/FieldViewMode';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

// The weekdays in the python numbering of the backend, monday is zero
export const WEEKDAYS = [
    { value: 0, label: 'date_time.days.short.monday' },
    { value: 1, label: 'date_time.days.short.tuesday' },
    { value: 2, label: 'date_time.days.short.wednesday' },
    { value: 3, label: 'date_time.days.short.thursday' },
    { value: 4, label: 'date_time.days.short.friday' },
    { value: 5, label: 'date_time.days.short.saturday' },
    { value: 6, label: 'date_time.days.short.sunday' },
];

const WeekdayCheckboxesField: React.FC<WeekdayCheckboxesFieldType & { data: FieldData, viewKey: string }> = ({ 
    name, type, label, data, defaultValue, helperText, tooltipText, viewInEditMode, alignment, isEditable, 
    disabled = false, emptyLabel, viewKey
}) => {
    const { t } = useTranslation();
    const { errorMessages, setUnsavedChanges } = useGlobalContext();
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([]);

    // Set the selected weekdays from the received data or the default value
    useEffect(() => {
        if (data && Array.isArray(data[name])) {
            setSelectedWeekdays(data[name]);
        } else if (defaultValue) {
            setSelectedWeekdays(defaultValue);
            setUpdatedData((prevData: any) => ({ ...prevData, [name]: defaultValue }));
        }
    }, [data, name]);

    // Toggles a weekday
    const handleToggle = (weekday: number) => {
        const updatedWeekdays = selectedWeekdays.includes(weekday)
            ? selectedWeekdays.filter(day => day !== weekday)
            : [...selectedWeekdays, weekday].sort((a, b) => a - b);
        setSelectedWeekdays(updatedWeekdays);
        setUpdatedData({ ...updatedData, [name]: updatedWeekdays });
        setUnsavedChanges(viewKey, JSON.stringify(updatedWeekdays) !== JSON.stringify(data?.[name] || []));
    };

    // Get the error message of the field
    const errorMessage = errorMessages?.[name];

    return (
        <FieldWrapper
            name={name}
            type={type}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) ? (
                // Edit mode
                <div className={`weekday-checkboxes ${errorMessage ? 'is-invalid' : ''}`}>
                    {WEEKDAYS.map(weekday => {
                        const checked = selectedWeekdays.includes(weekday.value);
                        const checkboxId = `${name}_${weekday.value}`;
                        return (
                            <div key={weekday.value} className='weekday-checkbox'>
                                <input
                                    type='checkbox'
                                    id={checkboxId}
                                    name={checkboxId}
                                    checked={checked}
                                    onChange={() => handleToggle(weekday.value)}
                                    disabled={disabled}
                                    className='hidden-checkbox'
                                />
                                <label htmlFor={checkboxId} className={`custom-checkbox ${checked ? 'checked' : ''}`}>
                                    {checked && <img src={CheckboxCheck} alt='CheckboxCheck' className='checkbox-check' />}
                                </label>
                                <label htmlFor={checkboxId} className='weekday-label'>{t(weekday.label)}</label>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // View mode
                <FieldViewMode 
                    value={selectedWeekdays.map(day => t(WEEKDAYS[day].label)).join(', ')} 
                    alignment={alignment}
                    emptyLabel={emptyLabel}
                />
            )}
        </FieldWrapper>
    )
};

export default WeekdayCheckboxesField;
